/**
 * Licensed workplace kit shared by every non-HQ interior.
 *
 * Placements stay local to each coordinate island. Render consumers exist only
 * while that room is active; measured collision remains registered
 * synchronously so traversal never depends on download timing.
 */
import * as pc from 'playcanvas'
import { getPropEntry } from '../assets'
import type { AssetZoneController } from '../assetZones'
import { registerCollider } from '../colliders'
import { BOARD_HALF_X, BOARD_HALF_Z, BOARD_WALL_HEIGHT } from './board'
import {
  CAFE_CENTRAL_PROMENADE,
  CAFE_COMMON_TABLE,
  CAFE_CONVERSATION_TABLES,
  CAFE_QUIET_ROOM,
  CAFE_QUIET_ROOM_DOOR_APPROACH,
  CAFE_QUIET_SEATING,
  CAFE_SERVICE_AISLE,
} from './cafeLayout'
import { CAMPUS_ROOMS } from './campusRooms'
import { createZonedProp, type ZonedPropHandle } from './dressing'
import {
  EXCHANGE_CAPACITY_STATION,
  EXCHANGE_CENTRAL_SPINE,
  EXCHANGE_COST_DOOR_APPROACH,
  EXCHANGE_COST_STUDIO,
  EXCHANGE_LIVE_FLOW_INTERACTION,
} from './exchangeLayout'
import {
  furnitureDimensions,
  furnitureFootprint,
  furnitureFootprintsOverlap,
  type FurnitureFootprint,
  type FurniturePlacement,
} from './furnitureGeometry'
import {
  LIBRARY_CEILING,
  LIBRARY_HALF_X,
  LIBRARY_HALF_Z,
  LIBRARY_MAP_COLLIDER,
  LIBRARY_STACK_COLLIDERS,
} from './libraryLayout'
import { glassRoomPanelLayout } from './interiorDressing'
import { OPS_HALF_X, OPS_HALF_Z, OPS_WALL_HEIGHT } from './ops'
import {
  WORKSHOP_BUILD_STATIONS,
  WORKSHOP_CENTRAL_SPINE,
  WORKSHOP_POD_RUGS,
  WORKSHOP_TEST_CELL,
  WORKSHOP_TEST_CELL_DOOR_APPROACH,
  WORKSHOP_TEST_TABLE,
  WORKSHOP_TOOL_SHELVES,
} from './workshopLayout'

export const WORKPLACE_ASSET_ZONE = 'workplace'
export type WorkplacePlotId = 'P1' | 'P2' | 'P4' | 'P5' | 'P6' | 'P7' | 'P8'

export const DATACENTRE_NOC_ROOM = {
  centerX: 8.5,
  centerZ: 6.4,
  width: 8,
  depth: 6,
} as const

export const DATACENTRE_NOC_DOOR_APPROACH: FurnitureFootprint = {
  minX: 7.65,
  maxX: 9.35,
  minZ: 8.9,
  maxZ: 10.25,
}

export const DATACENTRE_CENTRAL_SPINE: FurnitureFootprint = {
  minX: -1.2,
  maxX: 1.2,
  minZ: -9.2,
  maxZ: 11,
}

export const DATACENTRE_CORE_COLLIDER = {
  minX: -1.7,
  maxX: 1.7,
  minY: 0,
  maxY: 2.25,
  minZ: -8.2,
  maxZ: -4.8,
} as const

export const DATACENTRE_CABLE_SPINE_COLLIDER = {
  minX: -0.18,
  maxX: 0.18,
  minY: 0,
  maxY: 0.05,
  minZ: -4.6,
  maxZ: 8,
} as const

export const DATACENTRE_SERVER_CABINET_FILES = [
  'synty_office_server_cabinet_01_full.glb',
  'synty_office_server_cabinet_02_full.glb',
] as const

export const DATACENTRE_SERVICE_RACKS = [
  { id: 'west-south-outer', file: DATACENTRE_SERVER_CABINET_FILES[0], x: -9.3, z: 1.6, rotY: 0 },
  { id: 'west-south-inner', file: DATACENTRE_SERVER_CABINET_FILES[1], x: -7.7, z: 1.6, rotY: 0 },
  { id: 'west-north-outer', file: DATACENTRE_SERVER_CABINET_FILES[1], x: -9.3, z: -1.7, rotY: 180 },
  { id: 'west-north-inner', file: DATACENTRE_SERVER_CABINET_FILES[0], x: -7.7, z: -1.7, rotY: 180 },
  { id: 'east-south-inner', file: DATACENTRE_SERVER_CABINET_FILES[0], x: 4.7, z: 1.6, rotY: 0 },
  { id: 'east-south-outer', file: DATACENTRE_SERVER_CABINET_FILES[1], x: 6.3, z: 1.6, rotY: 0 },
  { id: 'east-north-inner', file: DATACENTRE_SERVER_CABINET_FILES[1], x: 4.7, z: -1.7, rotY: 180 },
  { id: 'east-north-outer', file: DATACENTRE_SERVER_CABINET_FILES[0], x: 6.3, z: -1.7, rotY: 180 },
] as const

export interface WorkplaceFurniturePlacement extends FurniturePlacement {
  id: string
  plotId: WorkplacePlotId
  /** Surface-mounted or non-blocking layer. */
  touch?: true
  castShadows?: true
}

interface RoomBounds {
  halfX: number
  halfZ: number
  ceiling: number
}

export const WORKPLACE_ROOM_BOUNDS: Readonly<Record<WorkplacePlotId, RoomBounds>> = {
  P1: { halfX: BOARD_HALF_X, halfZ: BOARD_HALF_Z, ceiling: BOARD_WALL_HEIGHT },
  P2: { halfX: OPS_HALF_X, halfZ: OPS_HALF_Z, ceiling: OPS_WALL_HEIGHT },
  P4: {
    halfX: CAMPUS_ROOMS.exchange.halfX,
    halfZ: CAMPUS_ROOMS.exchange.halfZ,
    ceiling: CAMPUS_ROOMS.exchange.height,
  },
  P5: {
    halfX: CAMPUS_ROOMS.workshop.halfX,
    halfZ: CAMPUS_ROOMS.workshop.halfZ,
    ceiling: CAMPUS_ROOMS.workshop.height,
  },
  P6: {
    halfX: CAMPUS_ROOMS.cafe.halfX,
    halfZ: CAMPUS_ROOMS.cafe.halfZ,
    ceiling: CAMPUS_ROOMS.cafe.height,
  },
  P7: { halfX: LIBRARY_HALF_X, halfZ: LIBRARY_HALF_Z, ceiling: LIBRARY_CEILING },
  P8: {
    halfX: CAMPUS_ROOMS.datacentre.halfX,
    halfZ: CAMPUS_ROOMS.datacentre.halfZ,
    ceiling: CAMPUS_ROOMS.datacentre.height,
  },
}

function item(
  plotId: WorkplacePlotId,
  id: string,
  file: string,
  x: number,
  z: number,
  rotY = 0,
  scale = 1,
  y = 0,
  touch?: true,
  castShadows?: true,
): WorkplaceFurniturePlacement {
  return { plotId, id, file, x, y, z, rotY, scale, touch, castShadows }
}

function fourSeatTable(
  plotId: WorkplacePlotId,
  prefix: string,
  x: number,
  z: number,
): WorkplaceFurniturePlacement[] {
  return [
    item(plotId, `${prefix}-rug`, 'synty_office_rug_01.glb', x, z, 90, 0.9, 0.01, true),
    item(plotId, `${prefix}-table`, 'synty_office_table_round_01.glb', x, z, 0, 1, 0, undefined, true),
    item(plotId, `${prefix}-chair-west`, 'synty_office_chair_01.glb', x - 1.3, z, 90),
    item(plotId, `${prefix}-chair-east`, 'synty_office_chair_01.glb', x + 1.3, z, 270),
    item(plotId, `${prefix}-chair-north`, 'synty_office_chair_01.glb', x, z - 1.3, 0),
    item(plotId, `${prefix}-chair-south`, 'synty_office_chair_01.glb', x, z + 1.3, 180),
  ]
}

function workstation(
  plotId: WorkplacePlotId,
  prefix: string,
  x: number,
  z: number,
  rotY = 0,
): WorkplaceFurniturePlacement[] {
  const chairOffset = rotY === 90
    ? [-1.35, 0]
    : rotY === 270
      ? [1.35, 0]
      : rotY === 180
        ? [0, -1.35]
        : [0, 1.35]
  return [
    item(plotId, `${prefix}-desk`, 'synty_office_desk_01.glb', x, z, rotY, 1, 0, undefined, true),
    item(
      plotId,
      `${prefix}-chair`,
      'synty_office_chair_01.glb',
      x + chairOffset[0],
      z + chairOffset[1],
      (rotY + 180) % 360,
    ),
    item(plotId, `${prefix}-monitor`, 'synty_office_monitor_double_01.glb', x, z, rotY, 0.75, 0.75, true),
    item(plotId, `${prefix}-lamp`, 'synty_office_desk_lamp_01.glb', x - 0.65, z, rotY, 0.8, 0.75, true),
  ]
}

function sixSeatConferenceTable(
  plotId: WorkplacePlotId,
  prefix: string,
  x: number,
  z: number,
): WorkplaceFurniturePlacement[] {
  return [
    item(plotId, `${prefix}-rug`, 'synty_office_rug_01.glb', x, z, 90, 1.2, 0.01, true),
    item(plotId, `${prefix}-table`, 'synty_office_table_conference_01.glb', x, z, 0, 0.92, 0, undefined, true),
    item(plotId, `${prefix}-chair-north-west`, 'synty_office_chair_01.glb', x - 1.15, z - 1.45, 180),
    item(plotId, `${prefix}-chair-north-east`, 'synty_office_chair_01.glb', x + 1.15, z - 1.45, 180),
    item(plotId, `${prefix}-chair-south-west`, 'synty_office_chair_01.glb', x - 1.15, z + 1.45),
    item(plotId, `${prefix}-chair-south-east`, 'synty_office_chair_01.glb', x + 1.15, z + 1.45),
    item(plotId, `${prefix}-chair-west`, 'synty_office_chair_01.glb', x - 2.35, z, 90),
    item(plotId, `${prefix}-chair-east`, 'synty_office_chair_01.glb', x + 2.35, z, 270),
  ]
}

export const WORKPLACE_FURNITURE: readonly WorkplaceFurniturePlacement[] = [
  // Board: the licensed strategy suite lives inside the glass room, while the
  // two side programs remain visibly distinct. The south-centre door and the
  // complete entry-to-live-wall spine stay clear.
  item('P1', 'board-strategy-rug', 'synty_office_rug_01.glb', 0, 1, 90, 1.28, 0.01, true),
  item('P1', 'board-strategy-table', 'synty_office_table_conference_01.glb', 0, 1, 0, 0.9, 0, undefined, true),
  item('P1', 'board-strategy-chair-north-west', 'synty_office_chair_01.glb', -1.25, -0.55, 180),
  item('P1', 'board-strategy-chair-north-east', 'synty_office_chair_01.glb', 1.25, -0.55, 180),
  item('P1', 'board-strategy-chair-south-west', 'synty_office_chair_01.glb', -1.25, 2.55),
  item('P1', 'board-strategy-chair-south-east', 'synty_office_chair_01.glb', 1.25, 2.55),
  item('P1', 'board-strategy-chair-west', 'synty_office_chair_01.glb', -2.45, 1, 90),
  item('P1', 'board-strategy-chair-east', 'synty_office_chair_01.glb', 2.45, 1, 270),
  item('P1', 'board-lounge-rug', 'synty_office_rug_01.glb', -9.7, 2.5, 90, 0.9, 0.01, true),
  item('P1', 'board-lounge-couch-west', 'synty_office_couch_03.glb', -11.2, 2.5, 90),
  item('P1', 'board-lounge-couch-east', 'synty_office_couch_03.glb', -8.25, 2.5, 270),
  item('P1', 'board-lounge-table', 'synty_office_coffee_table_01.glb', -9.7, 2.5, 90),
  item('P1', 'board-lounge-plant', 'synty_office_plant_08.glb', -12.3, 5.9, 0, 3.2),
  item('P1', 'board-review-table', 'synty_office_table_round_01.glb', 9.7, 2.4, 0, 1, 0, undefined, true),
  item('P1', 'board-review-chair-west', 'synty_office_chair_01.glb', 8.4, 2.4, 90),
  item('P1', 'board-review-chair-east', 'synty_office_chair_01.glb', 11, 2.4, 270),
  item('P1', 'board-review-screen', 'synty_office_whiteboard_01.glb', 13.65, 2.4, 270, 1.8, 0.8, true),
  item('P1', 'board-review-plant', 'synty_office_plant_08.glb', 12.3, 5.9, 0, 3.2),

  // Ops: three dispatch stations face the queue/status lane from the west
  // wall. The incident huddle occupies a glass room to the north-east while a
  // genuinely separate waiting lounge stays near the entry.
  ...workstation('P2', 'ops-dispatch-primary', -7.6, -5.7, 90),
  ...workstation('P2', 'ops-dispatch-triage', -7.6, -2.6, 90),
  ...workstation('P2', 'ops-dispatch-escalation', -7.6, 0.5, 90),
  item('P2', 'ops-incident-rug', 'synty_office_rug_01.glb', 6.7, -4.8, 90, 0.82, 0.01, true),
  item('P2', 'ops-incident-table', 'synty_office_table_round_01.glb', 6.7, -4.8, 0, 1, 0, undefined, true),
  item('P2', 'ops-incident-chair-west', 'synty_office_chair_01.glb', 5.4, -4.8, 90),
  item('P2', 'ops-incident-chair-east', 'synty_office_chair_01.glb', 8, -4.8, 270),
  item('P2', 'ops-incident-board', 'synty_office_whiteboard_01.glb', 6.7, -7.12, 0, 1.2, 1.05, true),
  item('P2', 'ops-waiting-rug', 'synty_office_rug_01.glb', 3.5, 2.5, 90, 0.72, 0.01, true),
  item('P2', 'ops-waiting-couch', 'synty_office_couch_03.glb', 5.2, 2.5, 90),
  item('P2', 'ops-waiting-table', 'synty_office_coffee_table_01.glb', 3.5, 2.5, 90),
  item('P2', 'ops-waiting-chair-north', 'synty_office_chair_01.glb', 2.3, 1.5, 180),
  item('P2', 'ops-waiting-chair-south', 'synty_office_chair_01.glb', 2.3, 3.5, 0),
  item('P2', 'ops-waiting-plant', 'synty_office_plant_08.glb', 7.5, 5.8, 0, 2.5),

  // Exchange: an occupied Cost Window glass studio, a low central Live Flow
  // coordination table, and a separate Capacity/review program. The licensed
  // groups stay clear of the south entry, glass doorway, and interaction edge.
  item('P4', 'exchange-cost-rug', 'synty_office_rug_01.glb', -9, -4.8, 0, 1.1, 0.01, true),
  ...workstation('P4', 'exchange-cost', -9, -5.4),
  item('P4', 'exchange-cost-visitor-west', 'synty_office_chair_01.glb', -10.3, -2.8, 180),
  item('P4', 'exchange-cost-visitor-east', 'synty_office_chair_01.glb', -7.7, -2.8, 180),
  item('P4', 'exchange-cost-board', 'synty_office_whiteboard_01.glb', -12.55, -5.3, 270, 1.4, 1, true),
  item('P4', 'exchange-cost-plant', 'synty_office_plant_08.glb', -11.6, -7, 0, 2.5),
  item('P4', 'exchange-flow-rug', 'synty_office_rug_01.glb', 0, -4.6, 90, 1.28, 0.01, true),
  item('P4', 'exchange-flow-table', 'synty_office_table_conference_01.glb', 0, -4.6, 0, 0.9, 0, undefined, true),
  item('P4', 'exchange-flow-chair-west', 'synty_office_chair_01.glb', -2.4, -4.6, 90),
  item('P4', 'exchange-flow-chair-east', 'synty_office_chair_01.glb', 2.4, -4.6, 270),
  item('P4', 'exchange-flow-chair-north', 'synty_office_chair_01.glb', 0, -6.25, 180),
  item('P4', 'exchange-flow-chair-south', 'synty_office_chair_01.glb', 0, -2.95),
  item('P4', 'exchange-flow-monitor', 'synty_office_monitor_double_01.glb', 0, -4.6, 0, 0.75, 0.75, true),
  item('P4', 'exchange-capacity-rug', 'synty_office_rug_01.glb', 9.05, -4.8, 0, 1.25, 0.01, true),
  ...workstation('P4', 'exchange-capacity-west', 7.6, -5.4),
  ...workstation('P4', 'exchange-capacity-east', 10.5, -5.4),
  item('P4', 'exchange-capacity-board', 'synty_office_whiteboard_01.glb', 13.65, -5.2, 90, 1.4, 1, true),
  item('P4', 'exchange-capacity-plant', 'synty_office_plant_08.glb', 12.1, -7.4, 0, 2.5),

  // Workshop: two mirrored build pods flank a 2.4 m central route to the
  // glass test cell. The north shelves form separate tool/plugin bays and the
  // licensed test table + screen replace the old blocking plinth/hidden board.
  ...WORKSHOP_BUILD_STATIONS.flatMap((station) =>
    workstation('P5', `workshop-build-${station.id}`, station.x, station.z, station.rotY)),
  ...WORKSHOP_POD_RUGS.map((rug) =>
    item(
      'P5',
      `workshop-build-${rug.id}-rug`,
      'synty_office_rug_01.glb',
      rug.x,
      rug.z,
      0,
      1.15,
      0.01,
      true,
    )),
  ...WORKSHOP_TOOL_SHELVES.map((shelf) =>
    item(
      'P5',
      `workshop-${shelf.id.startsWith('west') ? 'tool' : 'plugin'}-${shelf.id}-shelf`,
      'synty_office_shelf_01.glb',
      shelf.x,
      shelf.z,
    )),
  item(
    'P5',
    'workshop-test-table',
    'synty_office_table_round_01.glb',
    WORKSHOP_TEST_TABLE.x,
    WORKSHOP_TEST_TABLE.z,
    0,
    1.05,
    0,
    undefined,
    true,
  ),
  item(
    'P5',
    'workshop-test-screen',
    'synty_office_projector_screen_01.glb',
    0,
    -8.48,
    0,
    1.35,
    0.85,
    true,
  ),
  item('P5', 'workshop-entry-plant-west', 'synty_office_plant_08.glb', -12.5, 7.4, 0, 2.7),
  item('P5', 'workshop-entry-plant-east', 'synty_office_plant_08.glb', 12.5, 7.4, 0, 2.7),

  // Café: two distinct conversation clusters lead to one shared six-seat
  // project table. A measured glass quiet room contains its own lounge; every
  // group stays outside the arrival promenade, service aisle, and glass door.
  item('P6', 'cafe-counter-a', 'synty_office_kitchen_counter_01.glb', -11.5, -8.7),
  item('P6', 'cafe-counter-sink', 'synty_office_kitchen_counter_sink_01.glb', -10.5, -8.7),
  item('P6', 'cafe-counter-b', 'synty_office_kitchen_counter_01.glb', -9.5, -8.7),
  item('P6', 'cafe-coffee', 'synty_office_coffee_machine_01.glb', -10.8, -8.2, 0, 0.55, 0.95, true),
  item('P6', 'cafe-fridge', 'synty_office_fridge_01.glb', -7.8, -8.7),
  ...CAFE_CONVERSATION_TABLES.flatMap((table) =>
    fourSeatTable('P6', `cafe-conversation-${table.id}`, table.x, table.z)),
  ...sixSeatConferenceTable(
    'P6',
    'cafe-common',
    CAFE_COMMON_TABLE.x,
    CAFE_COMMON_TABLE.z,
  ),
  item('P6', 'cafe-quiet-rug', 'synty_office_rug_01.glb',
    CAFE_QUIET_SEATING.tableX, CAFE_QUIET_SEATING.tableZ, 90, 0.92, 0.01, true),
  item('P6', 'cafe-quiet-couch', 'synty_office_couch_03.glb',
    CAFE_QUIET_SEATING.couchX, CAFE_QUIET_SEATING.couchZ, 180),
  item('P6', 'cafe-quiet-table', 'synty_office_coffee_table_01.glb',
    CAFE_QUIET_SEATING.tableX, CAFE_QUIET_SEATING.tableZ),
  item('P6', 'cafe-quiet-chair-west', 'synty_office_chair_01.glb', 7.05, -5.35, 90),
  item('P6', 'cafe-quiet-chair-east', 'synty_office_chair_01.glb', 10.35, -5.35, 270),
  item('P6', 'cafe-quiet-plant', 'synty_office_plant_08.glb', 11.45, -8.05, 0, 2.4),

  // Library: a south index commons, mirrored reading bays, and a north
  // research/synthesis studio. Nothing enters the four-run stack field, so the
  // broad browsing aisles remain real circulation instead of set dressing.
  item('P7', 'library-west-couch', 'synty_office_couch_03.glb', -6.2, 11.2, 180),
  item('P7', 'library-west-table', 'synty_office_coffee_table_01.glb', -6.2, 9.7),
  item('P7', 'library-west-plant', 'synty_office_plant_08.glb', -9, 9.6, 0, 2.8),
  item('P7', 'library-east-couch', 'synty_office_couch_03.glb', 6.2, 11.2, 180),
  item('P7', 'library-east-table', 'synty_office_coffee_table_01.glb', 6.2, 9.7),
  item('P7', 'library-east-plant', 'synty_office_plant_08.glb', 9, 9.6, 0, 2.8),
  item('P7', 'library-index-board', 'synty_office_whiteboard_01.glb', 11.65, 7.2, 90, 1.2, 1, true),
  ...workstation('P7', 'library-research-west', -4.4, -10.6),
  ...workstation('P7', 'library-research-east', 4.4, -10.6),
  item('P7', 'library-synthesis-table', 'synty_office_table_round_01.glb', 0, -10.3, 0, 1, 0, undefined, true),
  item('P7', 'library-synthesis-chair-west', 'synty_office_chair_01.glb', -1.3, -10.3, 90),
  item('P7', 'library-synthesis-chair-east', 'synty_office_chair_01.glb', 1.3, -10.3, 270),
  item('P7', 'library-synthesis-board', 'synty_office_whiteboard_01.glb', 0, -12.65, 0, 1.5, 1, true),

  ...workstation('P8', 'datacentre-noc-a', 6.7, 5.5),
  ...workstation('P8', 'datacentre-noc-b', 10.2, 5.5),
  item('P8', 'datacentre-noc-board', 'synty_office_whiteboard_01.glb', 12.2, 3.8, 90, 1.2, 1, true),
  item('P8', 'datacentre-noc-plant', 'synty_office_plant_08.glb', 12, 8.4, 0, 2.5),
  ...DATACENTRE_SERVICE_RACKS.map((rack) => item(
    'P8',
    `datacentre-service-rack-${rack.id}`,
    rack.file,
    rack.x,
    rack.z,
    rack.rotY,
  )),
]

/** The 1.2 m entrance/exit spine in every specialist interior. */
export function workplaceEntrySpine(plotId: WorkplacePlotId): FurnitureFootprint {
  const room = WORKPLACE_ROOM_BOUNDS[plotId]
  return {
    minX: -0.6,
    maxX: 0.6,
    minZ: room.halfZ - 3,
    maxZ: room.halfZ,
  }
}

export type WorkplaceLayoutIssueKind =
  | 'missing-catalog-entry'
  | 'wrong-asset-zone'
  | 'missing-bounds'
  | 'outside-room'
  | 'not-grounded'
  | 'ceiling-overflow'
  | 'overlap'
  | 'blocked-entry-spine'
  | 'architecture-overlap'

export interface WorkplaceLayoutIssue {
  kind: WorkplaceLayoutIssueKind
  plotId: WorkplacePlotId
  itemIds: readonly string[]
}

export function validateWorkplaceFurniture(
  placements: readonly WorkplaceFurniturePlacement[] = WORKPLACE_FURNITURE,
): WorkplaceLayoutIssue[] {
  const issues: WorkplaceLayoutIssue[] = []
  const footprints = new Map<WorkplaceFurniturePlacement, FurnitureFootprint>()
  for (const placement of placements) {
    const entry = getPropEntry(placement.file)
    if (!entry) {
      issues.push({ kind: 'missing-catalog-entry', plotId: placement.plotId, itemIds: [placement.id] })
      continue
    }
    if (entry.runtime?.zone !== WORKPLACE_ASSET_ZONE) {
      issues.push({ kind: 'wrong-asset-zone', plotId: placement.plotId, itemIds: [placement.id] })
    }
    const footprint = furnitureFootprint(placement)
    const dimensions = furnitureDimensions(placement)
    if (!footprint || !dimensions) {
      issues.push({ kind: 'missing-bounds', plotId: placement.plotId, itemIds: [placement.id] })
      continue
    }
    footprints.set(placement, footprint)
    const room = WORKPLACE_ROOM_BOUNDS[placement.plotId]
    if (
      footprint.minX < -room.halfX + 0.2 ||
      footprint.maxX > room.halfX - 0.2 ||
      footprint.minZ < -room.halfZ + 0.2 ||
      footprint.maxZ > room.halfZ - 0.2
    ) {
      issues.push({ kind: 'outside-room', plotId: placement.plotId, itemIds: [placement.id] })
    }
    if (!placement.touch && (dimensions.bottomY < -0.001 || dimensions.bottomY > 0.02)) {
      issues.push({ kind: 'not-grounded', plotId: placement.plotId, itemIds: [placement.id] })
    }
    if (dimensions.topY > room.ceiling - 0.05) {
      issues.push({ kind: 'ceiling-overflow', plotId: placement.plotId, itemIds: [placement.id] })
    }
  }

  for (const plotId of Object.keys(WORKPLACE_ROOM_BOUNDS) as WorkplacePlotId[]) {
    const ground = placements.filter((placement) =>
      placement.plotId === plotId &&
      !placement.touch &&
      footprints.has(placement))
    const spine = workplaceEntrySpine(plotId)
    for (const placement of ground) {
      if (furnitureFootprintsOverlap(footprints.get(placement)!, spine)) {
        issues.push({ kind: 'blocked-entry-spine', plotId, itemIds: [placement.id] })
      }
      if (
        plotId === 'P4' &&
        [
          EXCHANGE_CENTRAL_SPINE,
          EXCHANGE_COST_DOOR_APPROACH,
          EXCHANGE_LIVE_FLOW_INTERACTION.approach,
          EXCHANGE_CAPACITY_STATION.approach,
          ...glassRoomPanelLayout(
            EXCHANGE_COST_STUDIO.centerX,
            EXCHANGE_COST_STUDIO.centerZ,
            EXCHANGE_COST_STUDIO.width,
            EXCHANGE_COST_STUDIO.depth,
          ).map((panel) => panel.collider),
        ].some((collider) =>
          furnitureFootprintsOverlap(footprints.get(placement)!, collider))
      ) {
        issues.push({ kind: 'architecture-overlap', plotId, itemIds: [placement.id] })
      }
      if (
        plotId === 'P7' &&
        [...LIBRARY_STACK_COLLIDERS, LIBRARY_MAP_COLLIDER].some((collider) =>
          furnitureFootprintsOverlap(footprints.get(placement)!, collider))
      ) {
        issues.push({ kind: 'architecture-overlap', plotId, itemIds: [placement.id] })
      }
      if (
        plotId === 'P5' &&
        [
          WORKSHOP_CENTRAL_SPINE,
          WORKSHOP_TEST_CELL_DOOR_APPROACH,
          ...glassRoomPanelLayout(
            WORKSHOP_TEST_CELL.centerX,
            WORKSHOP_TEST_CELL.centerZ,
            WORKSHOP_TEST_CELL.width,
            WORKSHOP_TEST_CELL.depth,
          ).map((panel) => panel.collider),
        ].some((collider) =>
          furnitureFootprintsOverlap(footprints.get(placement)!, collider))
      ) {
        issues.push({ kind: 'architecture-overlap', plotId, itemIds: [placement.id] })
      }
      if (
        plotId === 'P6' &&
        [
          CAFE_CENTRAL_PROMENADE,
          CAFE_SERVICE_AISLE,
          CAFE_QUIET_ROOM_DOOR_APPROACH,
          ...glassRoomPanelLayout(
            CAFE_QUIET_ROOM.centerX,
            CAFE_QUIET_ROOM.centerZ,
            CAFE_QUIET_ROOM.width,
            CAFE_QUIET_ROOM.depth,
          ).map((panel) => panel.collider),
        ].some((collider) =>
          furnitureFootprintsOverlap(footprints.get(placement)!, collider))
      ) {
        issues.push({ kind: 'architecture-overlap', plotId, itemIds: [placement.id] })
      }
      if (
        plotId === 'P8' &&
        [
          DATACENTRE_CENTRAL_SPINE,
          DATACENTRE_NOC_DOOR_APPROACH,
          DATACENTRE_CORE_COLLIDER,
          ...glassRoomPanelLayout(
            DATACENTRE_NOC_ROOM.centerX,
            DATACENTRE_NOC_ROOM.centerZ,
            DATACENTRE_NOC_ROOM.width,
            DATACENTRE_NOC_ROOM.depth,
          ).map((panel) => panel.collider),
        ].some((collider) =>
          furnitureFootprintsOverlap(footprints.get(placement)!, collider))
      ) {
        issues.push({ kind: 'architecture-overlap', plotId, itemIds: [placement.id] })
      }
    }
    for (let index = 0; index < ground.length; index++) {
      for (let other = index + 1; other < ground.length; other++) {
        if (furnitureFootprintsOverlap(
          footprints.get(ground[index])!,
          footprints.get(ground[other])!,
        )) {
          issues.push({
            kind: 'overlap',
            plotId,
            itemIds: [ground[index].id, ground[other].id],
          })
        }
      }
    }
  }
  return issues
}

export interface WorkplaceFurnishingHandle {
  setActive(active: boolean): void
  destroy(): void
  entities: readonly pc.Entity[]
}

export function buildWorkplaceFurnishing(
  app: pc.Application,
  root: pc.Entity,
  plotId: WorkplacePlotId,
  assetZones: AssetZoneController<pc.Asset>,
): WorkplaceFurnishingHandle {
  const batcher = app.batcher
  const batchGroupId = batcher?.addGroup(`workplace-furniture:${plotId}`, true, 32).id
  const handles: ZonedPropHandle[] = []
  if (plotId === 'P8') {
    registerCollider(DATACENTRE_CORE_COLLIDER)
    registerCollider(DATACENTRE_CABLE_SPINE_COLLIDER)
  }
  for (const placement of WORKPLACE_FURNITURE.filter((item) => item.plotId === plotId)) {
    const handle = createZonedProp(
      app,
      assetZones,
      WORKPLACE_ASSET_ZONE,
      placement.file,
      [placement.x, placement.y, placement.z],
      placement.rotY,
      placement.scale,
      root,
      { batchGroupId, castShadows: placement.castShadows },
    )
    handle.entity.name = `workplace-furniture-${plotId}-${placement.id}`
    handles.push(handle)

    const runtime = getPropEntry(placement.file)?.runtime
    const footprint = furnitureFootprint(placement)
    const dimensions = furnitureDimensions(placement)
    if (
      !placement.touch &&
      runtime?.collision.shape === 'box' &&
      footprint &&
      dimensions
    ) {
      registerCollider({
        minX: footprint.minX,
        maxX: footprint.maxX,
        minY: Math.max(0, dimensions.bottomY),
        maxY: dimensions.topY,
        minZ: footprint.minZ,
        maxZ: footprint.maxZ,
      })
    }
  }
  let active = false
  return {
    entities: handles.map((handle) => handle.entity),
    setActive(next) {
      if (next === active) return
      active = next
      for (const handle of handles) handle.setActive(next)
    },
    destroy() {
      for (const handle of handles) handle.destroy()
      if (batchGroupId !== undefined) {
        app.batcher?.removeGroup(batchGroupId)
      }
    },
  }
}
